import { PageDS, ToastContainerDS } from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import "../../../../i18n/i18n";
import { useNavigate } from "react-router-dom";
import { AddUserRole } from "./addUserRole";
import { ManagerCreation, ManagerCreationRole } from "../usersAPI";
import { AddUserInformations } from "./addUserInformation";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { selectCompany } from "../../homeSlice";
import { managersInCompanyGroupAsync, selectManagersInCompanyGroupStatus, userAsync } from "../usersSlice";
import { AddUserDepartmentModal } from "./addUserDepartmentModal";
import { DepartmentType } from "../../departments/departmentsApi";
import useFullScreen from "../../../../hooks/useFullScreen";

type Steps = "role" | "informations" | "department";

export type UserInformations = {
  firstName: string;
  lastName: string;
  email: string;
  job: string;
};

export const AddUserPage = () => {
  useFullScreen();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [step, setStep] = useState<Steps>("informations");
  const company = useAppSelector(selectCompany);
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [job, setJob] = useState<string>("");
  const managersInCompanyGroupStatus = useAppSelector(
    selectManagersInCompanyGroupStatus,
  );

  useEffect(() => {
    if (company && managersInCompanyGroupStatus === "idle") {
      void dispatch(managersInCompanyGroupAsync());
    }
  }, [dispatch, company, managersInCompanyGroupStatus]);

  const resetValue = useCallback(() => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setJob("");
  }, []);

  const onClose = useCallback(() => {
    resetValue();
    navigate("/home/users/management");
  }, [navigate, resetValue]);

  const onSubmitInformations = useCallback(
    (informations: UserInformations) => {
      setFirstName(informations.firstName);
      setLastName(informations.lastName);
      setEmail(informations.email);
      setJob(informations.job);
      setStep("role");
    },
    [setFirstName, setLastName, setEmail, setJob],
  );

  const onSubmit = useCallback(
    (role: ManagerCreationRole, departments?: DepartmentType[]) => {
      if (!company) return;
      if (role === "DEPARTMENT_USER" && departments && departments.length === 0) return;
      const userData: ManagerCreation = {
        firstName,
        lastName,
        email,
        job,
        companyUuid: company.uuid,
        role,
        requestedAt: new Date(),
        departmentsAccess: departments?.map((department) => department.uuid) ?? [],
      };
      resetValue();
      onClose();
      void dispatch(userAsync(userData));
    },
    [company, dispatch, email, firstName, job, lastName, onClose, resetValue],
  );

  const onSubmitUserRole = useCallback(
    (role: ManagerCreationRole) => {
      if (role === "DEPARTMENT_USER") {
        setStep("department");
      } else {
        onSubmit(role);
      }
    },
    [onSubmit, setStep],
  );

  const onSubmitUserDepartment = useCallback(
    (departments: DepartmentType[]) => {
      onSubmit("DEPARTMENT_USER", departments);
    },
    [onSubmit],
  );

  return (
    <PageDS
      isFullScreen
      title={t("users.modal.title") || ""}
      toaster={<ToastContainerDS />}
      titleElement={{
        closeButtonAction: onClose,
      }}
      withoutSupport
    >
      {step === "informations" ? (
        <AddUserInformations
          onSubmit={onSubmitInformations}
          firstName={firstName}
          lastName={lastName}
          email={email}
          job={job}
        />
      ) : (
        <></>
      )}
      {step === "role" ? (
        <AddUserRole
          onSubmit={onSubmitUserRole}
          onReturn={() => setStep("informations")}
        />
      ) : (
        <></>
      )}
      {step === "department" ? (
        <AddUserDepartmentModal
          onReturn={() => setStep("role")}
          onSubmit={onSubmitUserDepartment}
        />
      ) : (
        <></>
      )}
    </PageDS>
  );
};